import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, 
  faTimes, 
  faSearch,
  faUserShield,
  faArrowRight,
  faExclamationTriangle,
  faUser,
  faCamera,
  faTools,
  faMusic,
  faCalendarAlt,
  faChevronDown,
  faTimes as faClose,
  faCheck,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import {createBandNotification, createNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService'
import styles from './AssociatedActsSection.module.css';
import SearchBandsModal from '../common/SearchBandsModal';
import defaultProfilePic from '../../assets/Images/defaultUserProfilePicture.png';
import { doc, updateDoc, arrayUnion, arrayRemove, collection, getDocs, query, where, addDoc, deleteDoc, limit, orderBy, getDoc, setDoc, increment } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import UserContext from '../../context/UserContext';

// Role options for dropdown
const ROLE_OPTIONS = [
  // Artist subtypes
  { value: 'Bass Guitarist', label: 'Bass Guitarist', category: 'Artist' },
  { value: 'Cellist', label: 'Cellist', category: 'Artist' },
  { value: 'DJ', label: 'DJ', category: 'Artist' },
  { value: 'Dhol Player', label: 'Dhol Player', category: 'Artist' },
  { value: 'Dholak Player', label: 'Dholak Player', category: 'Artist' },
  { value: 'Drummer', label: 'Drummer', category: 'Artist' },
  { value: 'Flutist', label: 'Flutist', category: 'Artist' },
  { value: 'Ghatam Player', label: 'Ghatam Player', category: 'Artist' },
  { value: 'Guitarist', label: 'Guitarist', category: 'Artist' },
  { value: 'Harmonium Player', label: 'Harmonium Player', category: 'Artist' },
  { value: 'Keyboardist', label: 'Keyboardist', category: 'Artist' },
  { value: 'Mridangam Player', label: 'Mridangam Player', category: 'Artist' },
  { value: 'Music Producer', label: 'Music Producer', category: 'Artist' },
  { value: 'Percussionist', label: 'Percussionist', category: 'Artist' },
  { value: 'Rapper', label: 'Rapper', category: 'Artist' },
  { value: 'Santoor Player', label: 'Santoor Player', category: 'Artist' },
  { value: 'Sarod Player', label: 'Sarod Player', category: 'Artist' },
  { value: 'Saxophonist', label: 'Saxophonist', category: 'Artist' },
  { value: 'Shehnai Player', label: 'Shehnai Player', category: 'Artist' },
  { value: 'Sitar Player', label: 'Sitar Player', category: 'Artist' },
  { value: 'Tabla Player', label: 'Tabla Player', category: 'Artist' },
  { value: 'Trumpeter', label: 'Trumpeter', category: 'Artist' },
  { value: 'Veena Player', label: 'Veena Player', category: 'Artist' },
  { value: 'Violinist', label: 'Violinist', category: 'Artist' },
  { value: 'Vocalist', label: 'Vocalist', category: 'Artist' },
  
  // Crew subtypes
  { value: 'Backline Technician', label: 'Backline Technician', category: 'Crew' },
  { value: 'Dresser', label: 'Dresser', category: 'Crew' },
  { value: 'Drum Tech', label: 'Drum Tech', category: 'Crew' },
  { value: 'Guitar Tech', label: 'Guitar Tech', category: 'Crew' },
  { value: 'Keyboard Tech', label: 'Keyboard Tech', category: 'Crew' },
  { value: 'Light Engineer', label: 'Light Engineer', category: 'Crew' },
  { value: 'Lighting Technician', label: 'Lighting Technician', category: 'Crew' },
  { value: 'Makeup Artist', label: 'Makeup Artist', category: 'Crew' },
  { value: 'Merchandiser', label: 'Merchandiser', category: 'Crew' },
  { value: 'Monitor Engineer', label: 'Monitor Engineer', category: 'Crew' },
  { value: 'Photographer', label: 'Photographer', category: 'Crew' },
  { value: 'Rigger', label: 'Rigger', category: 'Crew' },
  { value: 'Road Manager', label: 'Road Manager', category: 'Crew' },
  { value: 'Security Personnel', label: 'Security Personnel', category: 'Crew' },
  { value: 'Sound Engineer', label: 'Sound Engineer', category: 'Crew' },
  { value: 'Stage Manager', label: 'Stage Manager', category: 'Crew' },
  { value: 'Stage Tech', label: 'Stage Tech', category: 'Crew' },
  { value: 'Tour Bus Driver', label: 'Tour Bus Driver', category: 'Crew' },
  { value: 'Videographer', label: 'Videographer', category: 'Crew' },
  
  // Manager subtypes
  { value: 'Tour Manager', label: 'Tour Manager', category: 'Manager' },
  { value: 'Artist Manager', label: 'Artist Manager', category: 'Manager' },
  
  // Vendor subtypes
  { value: 'Backline Provider', label: 'Backline Provider', category: 'Vendor' },
  { value: 'Light Vendor', label: 'Light Vendor', category: 'Vendor' },
  { value: 'Sound Vendor', label: 'Sound Vendor', category: 'Vendor' },
  { value: 'Stage and Production Vendor', label: 'Stage and Production Vendor', category: 'Vendor' },
  
  // Staff subtypes
  { value: 'Backline Equipment Coordinator', label: 'Backline Equipment Coordinator', category: 'Staff' },
  { value: 'Instrument Maintenance Technician', label: 'Instrument Maintenance Technician', category: 'Staff' },
  { value: 'Backline Logistics Coordinator', label: 'Backline Logistics Coordinator', category: 'Staff' },
  { value: 'Equipment Setup Technician', label: 'Equipment Setup Technician', category: 'Staff' },
  { value: 'Lighting Setup Technician', label: 'Lighting Setup Technician', category: 'Staff' },
  { value: 'Lighting Board Operator', label: 'Lighting Board Operator', category: 'Staff' },
  { value: 'Lighting Rig Technician', label: 'Lighting Rig Technician', category: 'Staff' },
  { value: 'DMX Systems Specialist', label: 'DMX Systems Specialist', category: 'Staff' },
  { value: 'Moving Light Technician', label: 'Moving Light Technician', category: 'Staff' },
  { value: 'PA System Technician', label: 'PA System Technician', category: 'Staff' },
  { value: 'Audio Setup Specialist', label: 'Audio Setup Specialist', category: 'Staff' },
  { value: 'Speaker Systems Technician', label: 'Speaker Systems Technician', category: 'Staff' },
  { value: 'Audio Equipment Coordinator', label: 'Audio Equipment Coordinator', category: 'Staff' },
  { value: 'FOH Systems Technician', label: 'FOH Systems Technician', category: 'Staff' },
  { value: 'Stage Construction Technician', label: 'Stage Construction Technician', category: 'Staff' },
  { value: 'Truss System Specialist', label: 'Truss System Specialist', category: 'Staff' },
  { value: 'Stage Equipment Coordinator', label: 'Stage Equipment Coordinator', category: 'Staff' },
  { value: 'Production Setup Coordinator', label: 'Production Setup Coordinator', category: 'Staff' },
  { value: 'Staging Logistics Specialist', label: 'Staging Logistics Specialist', category: 'Staff' },
  
  // Organization
  { value: 'Artist Management Company', label: 'Artist Management Company', category: 'Organization' }
];

// Generate month options for dropdowns
const generateMonthOptions = () => {
  return [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
};

// Generate year options for dropdowns
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 50; i--) {
    years.push({ value: i.toString(), label: i.toString() });
  }
  return years;
};

const AssociatedActsSection = ({ 
  associatedActs = [], 
  pastAssociatedActs = [], 
  onAssociatedActAdd, 
  onAssociatedActRemove,
  onPastAssociatedActAdd,
  onPastAssociatedActRemove,
  onBandUpdate,
  userData,
  showAlert
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('current');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isPastSearch, setIsPastSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // For Past Associated Acts
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  
  // For Current Associated Acts - "Since" date
  const [sinceMonth, setSinceMonth] = useState('');
  const [sinceYear, setSinceYear] = useState('');
  
  // For displaying acts - we'll start with the ones provided in props
  const [displayedCurrentActs, setDisplayedCurrentActs] = useState([]);
  const [displayedPastActs, setDisplayedPastActs] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  
  // Filter roles based on search
  const filteredRoles = ROLE_OPTIONS.filter(role => 
    role.label.toLowerCase().includes(roleSearch.toLowerCase())
  );
  
  // Get relevant functions from context
  const { addAssociatedAct, removeAssociatedAct, setUserData } = useContext(UserContext);

  // Check if user type should show this section
  const shouldShowAssociatedActs = () => {
    if (!userData) return false;
    return ['Artist', 'Crew', 'Manager'].includes(userData.userType);
  };

  // Load initial data including pending requests
  useEffect(() => {
    if (userData?.userId) {
      // Set displayed acts from the props first (which come from the main document arrays)
      setDisplayedCurrentActs(associatedActs || []);
      setDisplayedPastActs(pastAssociatedActs || []);
      
      // Check if there are more acts using counter fields
      // Show the "Load More" button if the count is greater than what's displayed
      setHasMoreCurrent((userData.associatedActsCount || 0) > (associatedActs?.length || 0));
      setHasMorePast((userData.pastAssociatedActsCount || 0) > (pastAssociatedActs?.length || 0));
      
      // Fetch pending association requests
      fetchPendingRequests();
    }
  }, [userData, associatedActs, pastAssociatedActs]);
  
  // Function to fetch pending association requests
  const fetchPendingRequests = async () => {
    if (!userData?.userId) return;
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingAssociationRequests');
      const requestsSnapshot = await getDocs(pendingRequestsRef);
      
      if (!requestsSnapshot.empty) {
        const currentRequests = [];
        const pastRequests = [];
        
        requestsSnapshot.docs.forEach(doc => {
          const request = {
            id: doc.id,
            ...doc.data(),
            isPending: true, // Mark as pending for UI
            docRef: doc.ref // Store reference for easy deletion
          };
          
          // Sort into current or past based on isPast flag
          if (request.isPast) {
            pastRequests.push(request);
          } else {
            currentRequests.push(request);
          }
        });
        
        // Add to displayed current acts with isPending flag
        setDisplayedCurrentActs(prev => {
          // Create a new array with pending requests that don't already exist
          const existingIds = new Set(prev.map(act => act.associationId));
          const newPendingActs = currentRequests.filter(req => !existingIds.has(req.associationId));
          
          // Combine existing acts with new pending acts
          return [...newPendingActs, ...prev];
        });
        
        // Add to displayed past acts with isPending flag
        setDisplayedPastActs(prev => {
          // Create a new array with pending requests that don't already exist
          const existingIds = new Set(prev.map(act => act.associationId));
          const newPendingActs = pastRequests.filter(req => !existingIds.has(req.associationId));
          
          // Combine existing acts with new pending acts
          return [...newPendingActs, ...prev];
        });
        
        // Also store separately if needed
        setPendingRequests([...currentRequests, ...pastRequests]);
      }
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to load pending association requests.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load more associated acts from subcollection
  const loadMoreActs = async (isPast = false) => {
    if (loadingMore || !userData?.userId) return;
    
    setLoadingMore(true);
    try {
      const db = getFirestoreInstance();
      const actsRef = collection(db, 'users', userData.userId, 'associatedActs');
      
      // Get the currently displayed acts
      const currentDisplayed = isPast ? displayedPastActs : displayedCurrentActs;
      
      // Skip already displayed items
      const actsQuery = query(
        actsRef,
        where('isPast', '==', isPast),
        orderBy('createdAt', 'desc'),
        limit(3) // Load next 6 acts
      );
      
      const actsSnapshot = await getDocs(actsQuery);
      
      if (!actsSnapshot.empty) {
        const allActs = actsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter out acts that are already displayed
        const displayedIds = currentDisplayed.map(act => act.id);
        const newActs = allActs.filter(act => !displayedIds.includes(act.id));
        
        // Update the displayed acts
        if (isPast) {
          const updatedPastActs = [...currentDisplayed, ...newActs];
          setDisplayedPastActs(updatedPastActs);
          // Update the "hasMore" flag based on counter
          setHasMorePast((userData.pastAssociatedActsCount || 0) > updatedPastActs.length);
        } else {
          // For current acts, we need to preserve the pending requests at the top
          const pendingActs = currentDisplayed.filter(act => act.isPending);
          const confirmedActs = currentDisplayed.filter(act => !act.isPending);
          
          const updatedCurrentActs = [...pendingActs, ...confirmedActs, ...newActs];
          setDisplayedCurrentActs(updatedCurrentActs);
          // Update the "hasMore" flag based on counter
          setHasMoreCurrent((userData.associatedActsCount || 0) > (updatedCurrentActs.length - pendingActs.length));
        }
      } else {
        // If no more acts are found, set hasMore to false
        if (isPast) {
          setHasMorePast(false);
        } else {
          setHasMoreCurrent(false);
        }
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} acts:`, error);
      showAlert({
        title: 'Error',
        message: `Failed to load more ${isPast ? 'past' : 'current'} associated acts.`,
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };

  

  const handleSearchSelection = async (selected) => {
    try {
      setIsLoading(true);
      
      // Ensure we have the correct type - use the type from selected, or default to 'artist'
      const actType = selected.type || 'artist';
      
      // Don't check if already associated - we now allow the same entity multiple times with different roles
      
      // For past associations, check required fields
      if (isPastSearch) {
        // Validation is now handled in the SearchBandsModal component
        await addPastAssociatedAct(selected);
      } else {
        // Validation is now handled in the SearchBandsModal component
        await addCurrentAssociatedAct(selected);
      }
    } catch (error) {
      console.error('Error adding associated act:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to add associated act. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setShowSearchModal(false);
      setIsPastSearch(false);
    }
  };


// Add a current associated act with the new subcollection pattern
const addCurrentAssociatedAct = async (selected) => {
  const db = getFirestoreInstance();
  
  try {
    setIsLoading(true);
    
    // Generate a unique association ID if not provided
    const associationId = selected.associationId || `${selected.id}-${selected.role}-${Date.now()}`;
    
    // Create the association request data
    const associationRequest = {
      id: selected.id,
      type: selected.type || 'artist',
      name: selected.name || selected.fullName,
      username: selected.username || selected.userName || '',
      photoUrl: selected.photoUrl || selected.profilePictureUrl || '',
      subType: selected.subType || '',
      genre: selected.genre || '',
      verified: selected.verified || false,
      role: selected.role,
      since: selected.since,
      status: 'pending',
      requestedAt: new Date().toISOString(),
      requestedBy: {
        userId: userData.userId,
        userName: userData.userName,
        fullName: userData.fullName,
        profilePictureUrl: userData.profilePictureUrl || '',
        userType: userData.userType
      },
      associationId: associationId
    };
    
    // Add to user's pendingAssociationRequests subcollection
    const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingAssociationRequests');
    const newRequestDoc = await addDoc(pendingRequestsRef, associationRequest);
    
    // Create notification data
    const notificationData = {
      type: NOTIFICATION_TYPES.ASSOCIATION_REQUEST,
      title: 'Association Request',
      message: `${userData.fullName} wants to add you in their associated acts. Role: ${selected.role}`,
      data: {
        requestData: {
          ...associationRequest,
          requestDocId: newRequestDoc.id // Include the document ID for reference
        },
        _displayData: {
          userRole: selected.role,
          userName: userData.userName,
          fullName: userData.fullName
        }
      },
      status: 'pending'
    };
    
    // Determine recipients and create notification
    if (selected.type === 'band') {
      // For bands, send to band admins
      const bandRef = doc(db, 'bands', selected.id);
      const bandDoc = await getDoc(bandRef);
      
      if (bandDoc.exists()) {
        const bandData = bandDoc.data();
        const adminMembers = bandData.members?.filter(member => member.role === 'admin') || [];
        
        if (adminMembers.length > 0) {
          // Create band notification - this correctly goes to the band's notifications subcollection
          await createBandNotification({
            bandId: selected.id,
            type: NOTIFICATION_TYPES.ASSOCIATION_REQUEST,
            title: notificationData.title,
            message: notificationData.message,
            recipientIds: adminMembers.map(admin => admin.userId),
            actionBy: {
              userId: userData.userId,
              userName: userData.userName,
              fullName: userData.fullName,
              profilePictureUrl: userData.profilePictureUrl || ''
            },
            data: notificationData.data
          });
        }
      }
    } else {
      // For individual users, send to that user
      // Use 'general' type to ensure it goes to the main notifications collection
      await createNotification(selected.id, 'general', notificationData);
    }
    
    // Add to displayed acts with pending status
    const pendingAct = {
      ...associationRequest,
      docRef: newRequestDoc,
      isPending: true
    };
    
    // Update displayed current acts with the new pending request at the top
    setDisplayedCurrentActs(prev => [pendingAct, ...prev]);
    
    // Also update the separate pending requests array
    setPendingRequests(prev => [pendingAct, ...prev]);
    
    showAlert({
      title: 'Request Sent',
      message: 'Association request sent successfully. Waiting for approval.',
      type: 'success'
    });
  } catch (error) {
    console.error('Error sending association request:', error);
    showAlert({
      title: 'Error',
      message: 'Failed to send association request. Please try again.',
      type: 'error'
    });
  } finally {
    setIsLoading(false);
  }
};

  // Add a past associated act with the new subcollection pattern
  const addPastAssociatedAct = async (selected) => {
    const db = getFirestoreInstance();
    
    try {
      setIsLoading(true);
      
      // Generate a unique association ID if not provided
      const associationId = selected.associationId || `${selected.id}-${selected.role}-${Date.now()}`;
      
      // Create the association request data
      const associationRequest = {
        id: selected.id,
        type: selected.type || 'artist',
        name: selected.name || selected.fullName,
        username: selected.username || selected.userName || '',
        photoUrl: selected.photoUrl || selected.profilePictureUrl || '',
        subType: selected.subType || '',
        genre: selected.genre || '',
        verified: selected.verified || false,
        role: selected.role,
        duration: selected.duration,
        isPast: true,
        status: 'pending',
        requestedAt: new Date().toISOString(),
        requestedBy: {
          userId: userData.userId,
          userName: userData.userName,
          fullName: userData.fullName,
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.userType
        },
        associationId: associationId
      };
      
      // Add to user's pendingAssociationRequests subcollection
      const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingAssociationRequests');
      const newRequestDoc = await addDoc(pendingRequestsRef, associationRequest);
      
      // Create notification data
      const notificationData = {
        type: NOTIFICATION_TYPES.ASSOCIATION_REQUEST,
        title: 'Past Association Request',
        message: `${userData.fullName} wants to add you in their past associated acts. Role: ${selected.role}`,
        data: {
          requestData: {
            ...associationRequest,
            requestDocId: newRequestDoc.id // Include the document ID for reference
          },
          _displayData: {
            userRole: selected.role,
            userName: userData.userName,
            fullName: userData.fullName
          }
        },
        status: 'pending'
      };
      
      // Determine recipients and create notification
      if (selected.type === 'band') {
        // For bands, send to band admins
        const bandRef = doc(db, 'bands', selected.id);
        const bandDoc = await getDoc(bandRef);
        
        if (bandDoc.exists()) {
          const bandData = bandDoc.data();
          const adminMembers = bandData.members?.filter(member => member.role === 'admin') || [];
          
          if (adminMembers.length > 0) {
            // Create band notification - this correctly goes to the band's notifications subcollection
            await createBandNotification({
              bandId: selected.id,
              type: NOTIFICATION_TYPES.ASSOCIATION_REQUEST,
              title: notificationData.title,
              message: notificationData.message,
              recipientIds: adminMembers.map(admin => admin.userId),
              actionBy: {
                userId: userData.userId,
                userName: userData.userName,
                fullName: userData.fullName,
                profilePictureUrl: userData.profilePictureUrl || ''
              },
              data: notificationData.data
            });
          }
        }
      } else {
        // For individual users, send to that user
        // Use 'general' type to ensure it goes to the main notifications collection
        await createNotification(selected.id, 'general', notificationData);
      }
      
      // Add to displayed acts with pending status
      const pendingAct = {
        ...associationRequest,
        docRef: newRequestDoc,
        isPending: true
      };
      
      // Update displayed past acts with the new pending request
      setDisplayedPastActs(prev => [pendingAct, ...prev]);
      
      showAlert({
        title: 'Request Sent',
        message: 'Past association request sent successfully. Waiting for approval.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error sending past association request:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to send past association request. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveClick = async (act, isPast = false) => {
    try {
      setIsLoading(true);
      
      // If this is a pending request, handle differently
      if (act.isPending) {
        await handleCancelPendingRequest(act);
        return;
      }
      
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);
      const associatedActsRef = collection(db, 'users', userData.userId, 'associatedActs');
      
      // Find the document in the subcollection by associationId if available
      let actQuery;
      
      if (act.associationId) {
        actQuery = query(
          associatedActsRef,
          where('associationId', '==', act.associationId),
          where('isPast', '==', isPast)
        );
      } else {
        // Make sure none of these values are undefined
        actQuery = query(
          associatedActsRef, 
          where('id', '==', act.id),
          where('type', '==', act.type || 'artist'), // Add default value
          where('role', '==', act.role || ''), // Add default value
          where('isPast', '==', isPast)
        );
      }
      
      const actSnapshot = await getDocs(actQuery);
      
      if (!actSnapshot.empty) {
        // Delete from subcollection
        for (const docSnapshot of actSnapshot.docs) {
          await deleteDoc(docSnapshot.ref);
        }
        
        // Update the main document arrays with new top 3
        if (isPast) {
          // Get new top 3 past acts
          const pastActsQuery = query(
            associatedActsRef,
            where('isPast', '==', true),
            orderBy('createdAt', 'desc'),
            limit(3)
          );
          
          const pastActsSnapshot = await getDocs(pastActsQuery);
          const top3PastActs = pastActsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Update main document and decrement the counter
          await updateDoc(userRef, {
            pastAssociatedActs: top3PastActs,
            pastAssociatedActsCount: increment(-1) // Decrement the counter
          });
          
          // Update displayed acts - use associationId for unique filtering if available
          if (act.associationId) {
            setDisplayedPastActs(prev => prev.filter(a => a.associationId !== act.associationId));
          } else {
            // Fallback to less precise filtering
            setDisplayedPastActs(prev => prev.filter(a => !(a.id === act.id && a.type === act.type && a.role === act.role)));
          }
          
          // Update hasMore flag based on the new count
          setHasMorePast((userData.pastAssociatedActsCount || 0) - 1 > top3PastActs.length);
          
          // Update the user data in context
          setUserData(prev => ({
            ...prev,
            pastAssociatedActs: top3PastActs,
            pastAssociatedActsCount: Math.max((prev.pastAssociatedActsCount || 0) - 1, 0)
          }));
          
          // Notify parent components
          if (onPastAssociatedActRemove) {
            onPastAssociatedActRemove(act.id);
          }
        } else {
          // Get new top 3 current acts
          const currentActsQuery = query(
            associatedActsRef,
            where('isPast', '==', false),
            orderBy('createdAt', 'desc'),
            limit(3)
          );
          
          const currentActsSnapshot = await getDocs(currentActsQuery);
          const top3CurrentActs = currentActsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Update main document and decrement the counter
          await updateDoc(userRef, {
            associatedActs: top3CurrentActs,
            associatedActsCount: increment(-1) // Decrement the counter
          });
          
          // Update displayed acts - use associationId for unique filtering if available
          if (act.associationId) {
            setDisplayedCurrentActs(prev => prev.filter(a => a.associationId !== act.associationId));
          } else {
            // Fallback to less precise filtering
            setDisplayedCurrentActs(prev => prev.filter(a => !(a.id === act.id && a.type === act.type && a.role === act.role)));
          }
          
          // Update hasMore flag based on the new count
          setHasMoreCurrent((userData.associatedActsCount || 0) - 1 > top3CurrentActs.length);
          
          // Update the user data in context
          setUserData(prev => ({
            ...prev,
            associatedActs: top3CurrentActs,
            associatedActsCount: Math.max((prev.associatedActsCount || 0) - 1, 0)
          }));
          
          // Notify parent components
          if (onAssociatedActRemove) {
            onAssociatedActRemove(act.id);
          }
          
          // Also update context if needed
          if (removeAssociatedAct) {
            await removeAssociatedAct(act.id, act.type);
          }
        }
        
        showAlert({
          title: 'Success',
          message: `${isPast ? 'Past a' : 'A'}ssociated act removed successfully`,
          type: 'success'
        });
      } else {
        showAlert({
          title: 'Error',
          message: 'Associated act not found',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error removing associated act:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to remove associated act. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle canceling a pending association request
  const handleCancelPendingRequest = async (act) => {
    try {
      const db = getFirestoreInstance();
      
      // Delete from pendingAssociationRequests collection
      if (act.docRef) {
        await deleteDoc(act.docRef);
      } else {
        // Try to find the document in the pendingAssociationRequests collection
        const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingAssociationRequests');
        let queryRef; // Renamed to avoid conflict with query function
        
        if (act.associationId) {
          queryRef = query(pendingRequestsRef, where('associationId', '==', act.associationId));
        } else {
          // Make sure none of these values are undefined
          queryRef = query(
            pendingRequestsRef, 
            where('id', '==', act.id),
            where('type', '==', act.type || 'artist'), // Add default value 
            where('role', '==', act.role || '') // Add default value
          );
        }
        
        const snapshot = await getDocs(queryRef);
        if (!snapshot.empty) {
          for (const doc of snapshot.docs) {
            await deleteDoc(doc.ref);
          }
        }
      }
      
      // Remove from UI - filter by associationId if available
      if (act.associationId) {
        setDisplayedCurrentActs(prev => prev.filter(a => a.associationId !== act.associationId));
        setPendingRequests(prev => prev.filter(a => a.associationId !== act.associationId));
      } else {
        setDisplayedCurrentActs(prev => 
          prev.filter(a => !(a.id === act.id && a.type === act.type && a.role === act.role))
        );
        setPendingRequests(prev => 
          prev.filter(a => !(a.id === act.id && a.type === act.type && a.role === act.role))
        );
      }
      
      // Try to remove any notifications sent to the target user/band
      try {
        if (act.type === 'band') {
          // For bands, we would need to find and remove the notification from the band's notifications
          // This is more complex and might be better handled server-side
        } else {
          // For individual users, we'd need to find the notification in their collection
          // This is also complex without knowing the notification ID
        }
      } catch (notifError) {
        console.error('Error removing notification:', notifError);
        // Continue even if notification removal fails
      }
      
      showAlert({
        title: 'Success',
        message: 'Pending request canceled successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error canceling pending request:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to cancel request. Please try again.',
        type: 'error'
      });
      throw error;
    }
  };
  
  const handleGoToMyBands = () => {
    navigate('/my-bands');
  };

  // Open search modal for adding a current or past associated act
  const openSearchModal = (isPast = false) => {
    // Reset form fields first
    setStartMonth('');
    setStartYear('');
    setEndMonth('');
    setEndYear('');
    setSinceMonth('');
    setSinceYear('');
    setSelectedRole('');
    
    // Set the state directly
    setIsPastSearch(isPast);
    setShowSearchModal(true);
  };

  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    const startMonthName = generateMonthOptions().find(m => m.value === startMonth)?.label.substring(0, 3) || '';
    const endMonthName = generateMonthOptions().find(m => m.value === endMonth)?.label.substring(0, 3) || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };
  
  // Format "since" date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    const monthName = generateMonthOptions().find(m => m.value === month)?.label.substring(0, 3) || '';
    
    return `Since ${monthName} ${year}`;
  };

  // If this user type shouldn't show associated acts section, return null
  if (!shouldShowAssociatedActs()) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Associated Acts</h2>
      <div className={styles.sectionDescription}>
        Add artists, bands, or collaborators you've worked with. To join a band or send a band membership request, go to <strong>My Bands</strong> page.
      </div>
      
      {/* Tabs for Current/Past */}
      <div className={styles.actsTabs}>
        <button 
          className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      <div className={styles.buttonContainer}>
        <button 
          className={styles.actionButton}
          onClick={handleGoToMyBands}
        >
          <FontAwesomeIcon icon={faArrowRight} />
          Go to My Bands
        </button>
        <button 
          className={styles.actionButton}
          onClick={() => openSearchModal(activeTab === 'past')}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add {activeTab === 'past' ? 'Past ' : ''}Associated Act
        </button>
      </div>

      {/* Display current associated acts */}
      {activeTab === 'current' && (
        <div className={styles.actGridContainer}>
          {displayedCurrentActs.length > 0 ? (
            <>
              <div className={styles.actGrid}>
                {displayedCurrentActs.map((act, index) => (
                  <div 
                    key={act.associationId || `current-${act.id}-${act.role || 'role'}-${index}`} 
                    className={`${styles.actCard} ${act.isPending ? styles.pendingCard : ''}`}
                  >
                    <div className={styles.cardContent}>
                      <img 
                        src={act.photoUrl || defaultProfilePic} 
                        alt={act.name}
                        className={styles.actPhoto}
                      />
                      <div className={styles.actInfo}>
                        <h3>{act.name}</h3>
                        {act.genre && <p className={styles.genre}>{act.genre}</p>}
                        <div className={styles.actType}>
                          {act.type === 'band' ? (
                            <span className={styles.bandBadge}>
                              <FontAwesomeIcon icon={faMusic} /> Band
                            </span>
                          ) : act.subType ? (
                            <span className={styles.subTypeBadge}>
                              <FontAwesomeIcon icon={act.type === 'crew' ? faTools : faUser} /> {act.subType}
                            </span>
                          ) : (
                            <span className={styles.artistBadge}>
                              <FontAwesomeIcon icon={faUser} /> Artist
                            </span>
                          )}
                        </div>
                        {act.isPending && (
                          <div className={styles.pendingBadge}>
                            <FontAwesomeIcon icon={faClock} /> Pending Approval
                          </div>
                        )}
                        {act.role && (
                          <div className={styles.roleBadge}>
                            <FontAwesomeIcon icon={faUserShield} />as a {act.role}
                          </div>
                        )}
                        {act.since && (
                          <div className={styles.sinceBadge}>
                            <FontAwesomeIcon icon={faClock} /> {formatSince(act.since)}
                          </div>
                        )}
                      </div>
                      <div className={styles.cardActions}>
                        <button 
                          className={styles.removeButton}
                          onClick={() => handleRemoveClick(act)}
                          title={act.isPending ? "Cancel Request" : "Remove"}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Load more button for current acts */}
              {hasMoreCurrent && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreActs(false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>No associated acts added yet. Click "Add Associated Act" to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Display past associated acts */}
      {activeTab === 'past' && (
        <div className={styles.actGridContainer}>
          {displayedPastActs.length > 0 ? (
            <>
              <div className={styles.actGrid}>
                {displayedPastActs.map((act, index) => (
                  <div 
                    key={act.associationId || `past-${act.id}-${act.role || 'role'}-${index}`} 
                    className={styles.actCard}
                  >
                    <div className={styles.cardContent}>
                      <img 
                        src={act.photoUrl || defaultProfilePic} 
                        alt={act.name}
                        className={styles.actPhoto}
                      />
                      <div className={styles.actInfo}>
                        <h3>{act.name}</h3>
                        {act.genre && <p className={styles.genre}>{act.genre}</p>}
                        <div className={styles.actType}>
                          {act.type === 'band' ? (
                            <span className={styles.bandBadge}>
                              <FontAwesomeIcon icon={faMusic} /> Band
                            </span>
                          ) : act.subType ? (
                            <span className={styles.subTypeBadge}>
                              <FontAwesomeIcon icon={act.type === 'crew' ? faTools : faUser} /> {act.subType}
                            </span>
                          ) : (
                            <span className={styles.artistBadge}>
                              <FontAwesomeIcon icon={faUser} /> Artist
                            </span>
                          )}
                        </div>
                        {act.role && (
                          <div className={styles.roleBadge}>
                            <FontAwesomeIcon icon={faUserShield} />as a {act.role}
                          </div>
                        )}
                        {act.duration && (
                          <div className={styles.durationBadge}>
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <span>{formatDuration(act.duration)}</span>
                          </div>
                        )}
                      </div>
                      <div className={styles.cardActions}>
                        <button 
                          className={styles.removeButton}
                          onClick={() => handleRemoveClick(act, true)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Load more button for past acts */}
              {hasMorePast && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreActs(true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>No past associated acts added yet. Click "Add Past Associated Act" to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Search Modal with role and date fields */}
      {showSearchModal && (
  <SearchBandsModal
    isOpen={showSearchModal}
    onClose={() => {
      setShowSearchModal(false);
      setIsPastSearch(false);
      setStartMonth('');
      setStartYear('');
      setEndMonth('');
      setEndYear('');
      setSinceMonth('');
      setSinceYear('');
      setSelectedRole('');
    }}
    onSelect={handleSearchSelection}
    isPastSearch={isPastSearch} // Pass this explicitly
    userData={userData} // Add this line to pass userData
  />
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
        </div>
      )}
    </div>
  );
};

export default AssociatedActsSection;